/**
 * R110.99 — SEO Franchise Carousel (non-intrusive injection).
 *
 * Renders up to 3 ACTIVE brand cards under any SEO template
 * (LocationLanding, IndustryLanding, VentureTypeLanding, InvestmentBandLanding).
 *
 * Contract:
 *   • Zero SSR/hydration impact — the whole component defers its network
 *     call to `requestIdleCallback` so LCP + INP stay green.
 *   • Silently renders NOTHING when the category has no active brands OR
 *     the category slug is missing (page-level fallback banner shows up
 *     in that path so we don't double-render).
 *   • Every card click routes to `/onboarding?category=<slug>&city=<city>`
 *     — the existing 8-step questionnaire pre-fills from these params.
 *
 * Props:
 *   ideaSlug     : string  — required. business idea / venture / category slug.
 *   cityName     : string? — optional target-city for match ranking.
 *   headline     : string? — optional override; defaults to
 *                             "Verified Franchise Brands · <cat>".
 *   limit        : number? — default 3.
 */
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, ShieldCheck, ArrowRight, Wallet, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { trackTeaserViewed } from "@/utils/tracker";

const inrCompact = (v) => {
  const n = Number(v || 0);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  return "₹" + n.toLocaleString("en-IN");
};

function BrandCard({ f, cityName }) {
  const investmentRange =
    f.minimum_investment && f.maximum_investment
      ? `${inrCompact(f.minimum_investment)} – ${inrCompact(f.maximum_investment)}`
      : f.minimum_investment
      ? `From ${inrCompact(f.minimum_investment)}`
      : "Investment on request";

  // Onboarding pre-fill: category slug + optional city. The onboarding page
  // reads these query params and preheats step 1 / step 3 of the 8-question
  // flow (see /onboarding source).
  const preheat = new URLSearchParams();
  if (f.mapped_business_idea_ids?.[0]) {
    preheat.set("category", f.mapped_business_idea_ids[0]);
  }
  if (cityName) preheat.set("city", cityName);
  preheat.set("brand", f.id);
  const ctaHref = `/onboarding?${preheat.toString()}`;

  return (
    <Card
      className="rounded-2xl border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all overflow-hidden flex flex-col"
      data-testid={`seo-franchise-card-${f.id}`}
    >
      <CardContent className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-indigo-700 font-bold">
              <Building2 className="h-3 w-3" /> Verified brand
            </div>
            <div className="mt-1 font-heading text-lg font-bold text-slate-900 line-clamp-1">
              {f.brand_name}
            </div>
          </div>
          {f.year_established ? (
            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 shrink-0">
              Est. {f.year_established}
            </Badge>
          ) : null}
        </div>

        <div className="mt-3 space-y-1.5 text-xs text-slate-700">
          <div className="flex items-center gap-1.5">
            <Wallet className="h-3.5 w-3.5 text-emerald-600" />
            <span className="font-medium">{investmentRange}</span>
          </div>
          {f.payback_months ? (
            <div className="flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
              <span>Payback ≈ {f.payback_months} months</span>
            </div>
          ) : null}
          {Array.isArray(f.target_cities) && f.target_cities.length > 0 ? (
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              <span className="line-clamp-1">
                Active in {f.target_cities.slice(0, 3).join(", ")}
                {f.target_cities.length > 3 ? " +more" : ""}
              </span>
            </div>
          ) : null}
        </div>

        <Link
          to={ctaHref}
          onClick={() => trackTeaserViewed(f.brand_name, f.id)}
          data-testid={`seo-franchise-cta-${f.id}`}
          className="mt-4 inline-flex items-center justify-center rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2.5 px-4 transition-colors"
        >
          Get free feasibility & connect
          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

export default function SeoFranchiseCarousel({
  ideaSlug,
  cityName = null,
  headline = null,
  limit = 3,
}) {
  const [rows, setRows] = useState(null);   // null = loading, [] = empty
  const fired = useRef(false);

  useEffect(() => {
    if (!ideaSlug || fired.current) return;
    fired.current = true;

    // Defer to idle so we never contest LCP.
    const run = () => {
      api.get(`/franchises/for-idea/${encodeURIComponent(ideaSlug)}`, {
        params: cityName ? { target_city: cityName, limit } : { limit },
      })
        .then((r) => setRows(Array.isArray(r.data?.franchises) ? r.data.franchises : []))
        .catch(() => setRows([]));
    };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(run, { timeout: 1200 });
    } else {
      setTimeout(run, 300);
    }
  }, [ideaSlug, cityName, limit]);

  // Loading + empty states are intentionally light — SEO pages should not
  // reserve empty vertical space that harms CLS. When empty we drop a soft
  // one-line banner that also serves as a CTA rescue.
  if (rows === null) {
    return (
      <section
        className="mt-10"
        data-testid="seo-franchise-carousel-loading"
        aria-hidden="true"
        style={{ minHeight: "1px" }}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <section
        className="mt-10"
        data-testid="seo-franchise-carousel-empty"
      >
        <div className="rounded-2xl border border-dashed border-emerald-300 bg-emerald-50/40 p-5 sm:p-6 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-700 max-w-2xl">
            <span className="font-heading font-bold text-slate-900">
              Explore Top Verified Business & Franchise Opportunities
              {cityName ? ` in ${cityName}` : ""}
            </span>
            <div className="mt-1 text-xs text-slate-600">
              New brands are being onboarded in this category. Get your free
              feasibility report to be first in line when they go live.
            </div>
          </div>
          <Link
            to={`/onboarding?category=${encodeURIComponent(ideaSlug)}${cityName ? `&city=${encodeURIComponent(cityName)}` : ""}`}
            data-testid="seo-franchise-carousel-empty-cta"
            className="inline-flex items-center rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2.5 px-5"
          >
            Get free feasibility
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      className="mt-10"
      data-testid="seo-franchise-carousel"
    >
      <div className="flex items-end justify-between gap-2 flex-wrap">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
          {headline || `Verified Franchise Brands${cityName ? ` in ${cityName}` : ""}`}
        </h2>
        <div className="text-xs text-slate-500 font-medium">
          <ShieldCheck className="inline h-3.5 w-3.5 text-emerald-600 mr-1" />
          Max 3 curated brands · Wallet-backed leads only
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.slice(0, limit).map((f) => (
          <BrandCard key={f.id} f={f} cityName={cityName} />
        ))}
      </div>
    </section>
  );
}
